import { useCallback } from 'react'
import { useMutation } from 'react-query'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import posthog from 'posthog-js'
import { rememberPaymentFlow } from 'utilities/analytics/paymentFlow'
import { addOnlinePayForOther, postGatewayPaymentOneItem } from 'services/easyPay'
import useGatewayHandoff from 'utilities/hooks/useGatewayHandoff'

/**
 * One-item gateway payment.
 *
 * Two very different screens share it: «پرداخت برای دیگری» and the fund's own
 * واریز/حق عضویت receipt (`pages/loans/transaction/footer`). Both have always
 * reported `flow_type: 'pay_for_other_gateway'`, so half of what looked like
 * pay-for-other traffic in PostHog was in fact ordinary deposits. `entry`
 * separates them; `flow_type` is deliberately left alone so the existing
 * series stays comparable across the change.
 */
export default function useGetMoneyPoolCashin(
    data: any,
    entry: 'deposit' | 'pay_for_other'
) {
    const navigate = useNavigate()
    const { locked, isLocked, lock, hold, release } = useGatewayHandoff()
    const { isLoading, mutate } = useMutation({
        mutationFn: (payload: any) => {
            // return addOnlinePayForOther(payload)
            return postGatewayPaymentOneItem(payload)
        },
        onSuccess: (result, variables) => {
            localStorage.setItem(
                'toBePaidTransactions',
                JSON.stringify({
                    type: 'pay-for-other',
                    data: data ? data : [],
                    //@ts-ignore
                    // pk: result.cashins[0].transaction.pk,
                    //@ts-ignore
                    // gateway: 0,
                })
            )
            const item = variables?.paymentItems?.[0]
            posthog.capture('payment_gateway_redirect', { flow_type: 'pay_for_other_gateway', entry })
            rememberPaymentFlow({
                flow_type: 'pay_for_other_gateway',
                amount:
                    Number(item?.itemAmount ?? 0) +
                    Number(item?.commission ?? 0),
                gateway: variables?.gateway?.uniqueCode ?? null,
                box_id: item?.box_id ?? null,
                box_type: 'mnp',
            })
            // The button stays down while the browser is on its way to the
            // bank — see `useGatewayHandoff`. This is the screen where one
            // payer's ten taps became ten Pasargad purchases (2026-09-17).
            hold()
            //@ts-ignore
            window.location.replace(result?.payment_link)
            // if (result?.cashins[0].transaction.pay_url) {
            //     // window.location.replace(result?.cashins[0].transaction.pay_url)
            // } else {
            //     toast.success('پرداخت با موفقیت انجام شد')
            //     navigate('/gateway-callback')
            // }
            toast.success('در حال انتقال به صفحه کارت به کارت')

        },
        onError: (error: any) => {
            release()
            // Surface the backend's specific message. An amount over the
            // کارت‌به‌کارت ceiling comes back as {"error": "سقف واریز ..."} — the
            // one thing the payer can act on — and «مشکلی پیش آمده» told
            // them nothing. Fall back to the generic toast when the body has no
            // string message (serializer errors are an object, network errors
            // have no body at all).
            const backendMessage = error?.response?.data?.error
            posthog.capture('payment_initiate_failed', {
                flow_type: 'pay_for_other_gateway',
                entry,
                error_message:
                    typeof backendMessage === 'string' ? backendMessage : null,
                status: error?.response?.status,
            })
            toast.error(
                typeof backendMessage === 'string' && backendMessage
                    ? backendMessage
                    : 'مشکلی پیش آمده!'
            )
        },
    })
    const pay = useCallback(
        (payload: any) => {
            // Refused by the ref, not by `isLoading`: a second tap in the same
            // tick lands before React has re-rendered `disabled`.
            if (isLocked()) return
            lock()
            mutate(payload)
        },
        [isLocked, lock, mutate]
    )
    return { mutate: pay, isLoading: isLoading || locked }
}
